import { useState, useEffect } from 'react';
import DesignSelection from './components/DesignSelection';
import TextPersonalization from './components/TextPersonalization';
import RecipientInput from './components/RecipientInput';
import SendingAnimation from './components/SendingAnimation';
import Header from './components/Header';

export default function Home() {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDesign, setSelectedDesign] = useState<number | null>(null);
  const [valentineText, setValentineText] = useState('');
  const [recipient, setRecipient] = useState('');

  // Автоматическая прокрутка вверх при смене шага
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleDesignSelect = (designId: number) => {
    setSelectedDesign(designId);
    setTimeout(() => setCurrentStep(2), 500);
  };

  const handleTextComplete = (text: string) => {
    setValentineText(text);
    setCurrentStep(3);
  };

  const handleRecipientComplete = (username: string) => {
    setRecipient(username);
    setCurrentStep(4);
  };

  const handleCreateNew = () => {
    setCurrentStep(1);
    setSelectedDesign(null);
    setValentineText('');
    setRecipient('');
  };

  return (
    <div className="min-h-screen bg-[#17212b] relative overflow-hidden">
      {/* Telegram-style animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 text-pink-500 opacity-10 animate-float">
          <i className="ri-heart-fill text-6xl"></i>
        </div>
        <div className="absolute top-40 right-20 text-rose-500 opacity-10 animate-float-delayed">
          <i className="ri-heart-fill text-8xl"></i>
        </div>
        <div className="absolute bottom-32 left-1/4 text-purple-500 opacity-10 animate-float">
          <i className="ri-heart-fill text-7xl"></i>
        </div>
        <div className="absolute top-1/3 right-1/3 text-pink-400 opacity-10 animate-float-delayed">
          <i className="ri-heart-fill text-5xl"></i>
        </div>
      </div>

      <Header currentStep={currentStep} />

      <main className="relative z-10 container mx-auto px-4 py-8">
        {/* Telegram-style Progress indicator */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                  currentStep >= step 
                    ? 'bg-[#2AABEE] text-white shadow-lg scale-110' 
                    : 'bg-[#232E3C] text-gray-500 border-2 border-[#2C3847]'
                }`}>
                  {step}
                </div>
                {step < 4 && (
                  <div className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                    currentStep > step ? 'bg-[#2AABEE]' : 'bg-[#2C3847]'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3 text-sm font-medium">
            <span className={currentStep >= 1 ? 'text-[#2AABEE]' : 'text-gray-500'}>Дизайн</span>
            <span className={currentStep >= 2 ? 'text-[#2AABEE]' : 'text-gray-500'}>Текст</span>
            <span className={currentStep >= 3 ? 'text-[#2AABEE]' : 'text-gray-500'}>Получатель</span>
            <span className={currentStep >= 4 ? 'text-[#2AABEE]' : 'text-gray-500'}>Отправка</span>
          </div>
        </div>

        {/* Step content */}
        <div className="max-w-7xl mx-auto">
          {currentStep === 1 && (
            <DesignSelection onSelect={handleDesignSelect} />
          )}
          
          {currentStep === 2 && selectedDesign && (
            <TextPersonalization 
              designId={selectedDesign}
              onComplete={handleTextComplete}
              onBack={() => setCurrentStep(1)}
            />
          )}
          
          {currentStep === 3 && (
            <RecipientInput 
              designId={selectedDesign!}
              text={valentineText}
              onComplete={handleRecipientComplete}
              onBack={() => setCurrentStep(2)}
            />
          )}
          
          {currentStep === 4 && (
            <SendingAnimation 
              designId={selectedDesign!}
              text={valentineText}
              recipient={recipient}
              onCreateNew={handleCreateNew}
            />
          )}
        </div>
      </main>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}